import { Component, OnDestroy, OnInit, inject, ElementRef, ViewChild, AfterViewInit, HostListener, effect } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { FilterToolbarConfig } from 'src/app/shared/components/ui/filter-toolbar/filter-toolbar.component';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { StorageService } from 'src/app/storage.service';
import { ContentService } from 'src/app/views/content/content.service';
import { CourseCardData } from '../../../../shared/components/course-card/course-card.dto';
import { CreateKnowledgeComponent } from '../../../content/create-knowledge/create-knowledge.component';
import { MySpaceService } from '../../../my-space/my-space.service';

@Component({
  selector: 'app-courses-list',
  templateUrl: './courses-list.component.html',
  styleUrls: ['./courses-list.component.scss'],
  standalone: false,
})
export class CoursesListComponent implements OnInit, OnDestroy, AfterViewInit {
  private sidenavService = inject(SidenavService);
  private hostRef = inject(ElementRef);

  @ViewChild('scrollObserver') scrollObserver!: ElementRef;
  private observer: IntersectionObserver;
  private isObserverIntersecting = false;

  displayedCourses: CourseCardData[];
  courses = [];
  createMode;
  createModeSubscription;
  searchTerm = '';
  cacheDBSubscription: Subscription;
  courseEventSub: Subscription;
  translateSub;
  loading;
  maxusers;
  pageIndex = 0;
  pageSize = 20;
  totalSize = 0;
  isSmallSceen = this.sidenavService.isSmallOrTablet;
  selectedContentType: string = 'knowledge';
  filterOptions: FilterToolbarConfig[] = [
    { value: 'knowledge', label: 'Global.Knowledge', icon: 'library_books' },
    { value: 'archive', label: 'Archive.Archives', icon: 'archive' },
  ];

  constructor(
    private appService: AppService,
    private dialog: MatDialog,
    private mySpaceService: MySpaceService,
    private translate: TranslateService,
    private storageService: StorageService,
    private contentService: ContentService
  ) {
    this.courseEventSub = this.mySpaceService.courseEvent$.subscribe(() => {
      this.getCourses();
    });
    this.translateSub = this.translate.onLangChange.subscribe((params) => {
      this.getCourseNameAndDescChunkMainEditions();
    });
    this.cacheDBSubscription = this.storageService.updatedCacheData$.subscribe((res) => {
      const table = res.table;
      if (table === 'courseList') {
        this.storageService.find('courseList', [`${this.pageIndex}_${this.pageSize}_${this.selectedContentType}`]).then((res) => {
          const newCourses = res[0]?.courses || [];
          if (this.pageIndex === 0) {
            this.courses = newCourses;
          } else {
            this.courses = [...this.courses, ...newCourses];
          }
          this.totalSize = res[0]?.total || 0;
          this.getCourseNameAndDescChunkMainEditions();
        });
      }
    });

    // Control visibility of global toggle button based on screen size on this page
    effect(() => {
      const isSmall = this.isSmallSceen();
      this.sidenavService.showGlobalToggle.set(!isSmall);
    });
  }

  ngOnInit() {
    this.getCourses();
    this.createModeSubscription = this.appService.createMode$.subscribe((createMode) => (this.createMode = createMode));
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.createModeSubscription.unsubscribe();
    this.courseEventSub.unsubscribe();
    this.translateSub.unsubscribe();
    this.cacheDBSubscription.unsubscribe();

    // Restore global toggle button when navigating away
    this.sidenavService.showGlobalToggle.set(true);
  }

  ngAfterViewInit() {
    this.setupIntersectionObserver();
  }

  setupIntersectionObserver() {
    const options = {
      root: this.hostRef.nativeElement,
      rootMargin: '150px',
      threshold: 0.0,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.isObserverIntersecting = entry.isIntersecting;
        if (entry.isIntersecting && !this.loading && this.courses?.length < this.totalSize) {
          this.pageIndex++;
          this.getCourses(true);
        }
      });
    }, options);

    if (this.scrollObserver) {
      this.observer.observe(this.scrollObserver.nativeElement);
    }
  }

  getCourses(isLoadMore = false): void {
    if (!isLoadMore) {
      this.pageIndex = 0;
    }
    this.loading = true;
    this.contentService.getCourses(this.pageIndex, this.pageSize, this.selectedContentType).then((sub) =>
      sub.subscribe({
        next: (res) => {
          this.loading = false;
          if (this.pageIndex === 0) {
            this.courses = res.courses || [];
          } else {
            this.courses = [...this.courses, ...(res.courses || [])];
          }
          this.totalSize = res.total || 0;
          this.getCourseNameAndDescChunkMainEditions();

          setTimeout(() => {
            if (this.isObserverIntersecting && !this.loading && this.courses?.length < this.totalSize) {
              this.pageIndex++;
              this.getCourses(true);
            }
          }, 100);
        },
        error: (err) => {
          this.loading = false;
          console.error(err);
        },
      })
    );
  }

  getCourseNameAndDescChunkMainEditions() {
    if (!this.courses) {
      return;
    }
    this.courses = this.courses.map((course) => {
      if (course.__name) {
        course.actualName = course.__name.editions.find((edition) => edition.language === this.translate.currentLang).content;
      }
      if (course.__description) {
        course.actualDescription = course.__description.editions.find((edition) => edition.language === this.translate.currentLang).content;
      }
      return course;
    });
    this.filter();
  }
  filter() {
    const filteredCourses = this.courses.filter(
      (course) =>
        (course.actualName || course.name || '').toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        (course.actualDescription || course.description || '').toLowerCase().includes(this.searchTerm.toLowerCase())
    );
    this.displayedCourses = filteredCourses.map((course) => ({
      _id: course._id,
      name: course.name,
      actualName: course.actualName,
      description: course.description,
      actualDescription: course.actualDescription,
      isAdmin: course.isAdmin,
      shareLevel: course.shareLevel,
      shareToken: course.shareToken,
    }));
  }

  openCreateCourseDialog() {
    this.dialog
      .open(CreateKnowledgeComponent, {
        width: '90vw',
        maxWidth: '800px',
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((res) => {
        if (res === 'ok') {
          this.getCourses();
        }
      });
  }


  onSelectionChange(selection: string) {
    this.selectedContentType = selection;
    this.pageIndex = 0;
    this.getCourses();
  }
}
